import { GstAmountValues } from "./Calculation";
import { Contact } from "./Contact";
import { DossierTransactionSpentType } from "./DossierTransaction";
import { PaginationResponse } from "./Pagination";
import { State } from "./State";
import { PowerPlantType } from "./Vehicles";

export const financierSpousalDeclarationWhiteList: string[] = [];

export const ASSET_AND_LIABILITY_ID_PROPERTY = 100000000;
export const ASSET_AND_LIABILITY_ID_VEHICLE = 100000002;
export const ASSET_AND_LIABILITY_ID_LOAN = 100000005;
export const ASSET_AND_LIABILITY_ID_DEBT = 100000007;
export const ASSET_AND_LIABILITY_ID_ASSET_AND_DEBT = 100000009;

export const ACTION_CONFIRMATION_EVENT_IDS = {
  EMPLOYER_ACCOUNT_SETUP: "100000000",
  FINANCE_APPLICATION: "100000001",
  DIRECT_DEBIT: "100000002",
  PACKAGING_AGREEMENT: "100000005",
  VEHICLE_SOURCING_DRIVER_SOURCED_VEHICLE: "100000027",
  VEHICLE_SOURCING_REQUEST_VEHICLE_PRICING: "100000028",
  VEHICLE_SOURCING_DRIVER_WILL_SOURCE_VEHICLE: "100000029",
};

export type DossierStatus =
  | "Active"
  | "New"
  | "Closed"
  | "Closing"
  | "Cancelled";
export type OpportunityStatus = "open" | "won" | "lost";
export type DossierType =
  | "new"
  | "renovation"
  | "refinance"
  | "sale-and-leaseback"
  | "used";
export type DossierEventVisibility =
  | "visible"
  | "not-visible"
  | "after-completion"
  | "current-past-phases";
export type NextDriverAction =
  | "request-employer-setup"
  | "finance-application"
  | "direct-debit"
  | "packaging-agreement"
  | "no-action-required"
  | "select-vehicle-sourcing-method";
export type DossierEventTriggeredBy = "driver" | "employer" | "auto-ux";
export type EmployerUsesAutoUXOption = "yes" | "no" | "unsure";
export type FinanceStatus =
  | "new"
  | "submit-attempted"
  | "entered"
  | "approval-requested"
  | "active"
  | "rejected";
export type DriversLicenseType =
  | "full"
  | "provisional"
  | "learner"
  | "heavy-vehicle"
  | "none";
export type AustralianResidencyStatus =
  | "citizen"
  | "nz-citizen"
  | "permanent-resident"
  | "working-visa"
  | "other";
export type MaritalStatus = "single" | "married" | "defacto";
export type AddressResidenceStatus =
  | "own-outright"
  | "own-mortgage"
  | "rent"
  | "boarding"
  | "live-with-relatives";
export type EmploymentType =
  | "permanent-ft"
  | "permanent-pt"
  | "casual"
  | "contractor"
  | "partner"
  | "self-employed"
  | "director"
  | "unemployed"
  | "other";
export type PayFrequency = "weekly" | "fortnightly" | "monthly" | "bi-monthly";

export type DossierStatusGroup = "open" | "active" | "activated" | "cancelled";

export const DossierStatusGroupMap: Record<
  DossierStatusGroup,
  DossierStatus[]
> = {
  open: ["New"],
  activated: ["Active", "Closed", "Closing"],
  active: ["Active", "Closing"],
  cancelled: ["Cancelled"],
};

export const DossierStatusLabelMap: Record<DossierStatus, string> = {
  Active: "Active",
  New: "New",
  Closed: "Terminated",
  Closing: "Terminating",
  Cancelled: "Cancelled",
};

export const DossierStatusColorMap: Record<
  DossierStatus,
  | "success"
  | "tertiaryGreen"
  | "tertiaryDarkBlue"
  | "error"
  | "info"
  | "secondary"
  | "outline"
> = {
  Active: "tertiaryGreen",
  New: "secondary",
  Closed: "outline",
  Closing: "secondary",
  Cancelled: "outline",
};

export type VehicleSourcingOption =
  | "self-sourced"
  | "request-sourcing"
  | "will-self-source";

export type DossierTerminationReason =
  | "employer-terminated-employment"
  | "employee-terminated-employment"
  | "moving-to-a-new-employer"
  | "resigned"
  | "redundancy-or-employer-termination"
  | "replacing-vehicle"
  | "selling-vehicle"
  | "keeping-vehicle"
  | "other";

export const DossierTerminationReasonLabelMap: Record<
  DossierTerminationReason,
  string
> = {
  "employer-terminated-employment": "Employer Terminated Employment",
  "employee-terminated-employment": "Employee Terminated Employment",
  "moving-to-a-new-employer": "Moving to a New Employer",
  resigned: "Resigned",
  "redundancy-or-employer-termination": "Redundancy or Employer Termination",
  "replacing-vehicle": "Replacing Vehicle",
  "selling-vehicle": "Selling Vehicle",
  "keeping-vehicle": "Keeping Vehicle",
  other: "Other",
};

export type DossierTerminationRequestedBy = "driver" | "employer";

export type CategoryBalance = {
  budget: number;
  expenses: number;
  balance: number;
};

export type CategoryBalances = Record<
  DossierTransactionSpentType | "total",
  CategoryBalance
>;

export type Opportunity = {
  id: string;
  name: string;
  dossierId: string;
  catchEContractId: string | null;
  packagingAgreementRequested: boolean;
  driverRequestedEmployerSetup: boolean;
  financeApprovalEntered: boolean;
  employerExecutedPackagingAgreement: boolean;
  employerRejecetedPackaging: boolean;
  driverExecutedPackagingAgreement: boolean;
  driverSourcedVehicle: boolean;
  newAccountName: string;
  parentAccountId: string;
  parentAccountName: string;
  primaryQuoteId: string | null;
  status: OpportunityStatus;
  canCreateQuote: boolean;
  catchEContactUrl: string | null;
  referralCode: string | null;
  referrerId: string | null;
  includeNonDeductibleInLCC: boolean | null;
  includeDisposalInLCC: boolean | null;
  driverSecuredVehicle: boolean;
  taxInvoiceRequested: boolean;
  hasDriverVehicleOrder: boolean;
  driverRequestedVehiclePricing: boolean;
  vehiclePricingRequested: boolean;
  driverVehicleOrderUploaded: boolean;
};

export type DossierClosureStatus = "aware" | "unaware" | "rerequested";

export type Dossier = {
  id: string;
  crmContactId: string;
  crmContactName: string;
  opportunityId: string;
  employerId: string | null;
  identity: string;
  name: string;
  status: DossierStatus;
  type: DossierType;
  startDate: string;
  endDate: string;
  createdOn: string;
  lastTransactionSyncDate: string | null;
  lastFullTransactionSyncDate: string | null;
  vehicleMakeName: string;
  vehicleModelName: string;
  vehicleModelyear: string;
  vehicleVariantName: string;
  vehicleVariantImage: string;
  vehicleRegistrationNumber: string | null;
  noteToDriver: string | null;
  quoteCount?: number;
  opportunity?: Opportunity;
  contact?: Contact;
  hasLockedQuote: boolean;
  availableBalance: number | null;
  pendingClaimBalance: GstAmountValues;
  availableBalanceExcludingClaims: GstAmountValues;
  currentBalance: number | null;
  canClaimPublicCharging: boolean | null;
  canClaimHomeCharging: boolean | null;
  lastClaimedOdo: number | null;
  powerPlantType: PowerPlantType | null;
  categoryBalances: CategoryBalances | null;
  closureStatus: DossierClosureStatus | null;
  terminationId: string | null;
  annualSavings: number | null;
  annualisedPayrollTaxSaving: number | null;
};

export type DossierSavingsSummary = Pick<
  Dossier,
  | "id"
  | "crmContactName"
  | "vehicleMakeName"
  | "vehicleModelName"
  | "vehicleModelyear"
  | "vehicleVariantName"
  | "annualSavings"
  | "annualisedPayrollTaxSaving"
>;

export type DossierTermination = {
  id: string;
  dossierId: string;

  lastDateOfEmployment: string | null;
  endDate: string | null;
  terminationReason: DossierTerminationReason;
  leavingEmployer: boolean;
  terminationDateSet: boolean;

  // Driver only fields
  noteToDriver: string | null;
  noteFromDriver: string | null;
  fuelCardTransactionsToBeLoaded: number | null;
  currentBalanceExGst: number | null;
  projectedBalance: number | null;
  nextFinancePaymentDue: string | null;
  lastDateOfInsuranceCoverage: string | null;
  cashBalanceFinalised: boolean;

  // Employer only fields
  hasTerminationInvoice: boolean;
  terminationInvoiceSentDate: string | null;
  terminationInvoiceName: string | null;
  terminationInvoiceId: string | null;
  hasTerminationPayrollAdvice: boolean;
  terminationPayrollAdviceSentDate: string | null;
  terminationPayrollAdviceName: string | null;
  targetLastPayrollDate: string | null;
  requestingUserId: string;
  requestedBy: DossierTerminationRequestedBy;
  requestedByContactName: string | null;
  requestedAt: string | null;
  noteToEmployer: string | null;
  noteFromEmployer: string | null;
  driverNotifiedOfTermination: boolean;
};

export type DossierTerminationForEmployer = Omit<
  DossierTermination,
  | "noteToDriver"
  | "noteFromDriver"
  | "fuelCardTransactionsToBeLoaded"
  | "currentBalanceExGst"
  | "projectedBalance"
  | "nextFinancePaymentDue"
  | "lastDateOfInsuranceCoverage"
  | "cashBalanceFinalised"
>;

export type DossierTerminationForDriver = Omit<
  DossierTermination,
  | "requestedBy"
  | "requestingUserId"
  | "requestedByContactName"
  | "requestedAt"
  | "targetLastPayrollDate"
  | "hasTerminationInvoice"
  | "terminationInvoiceSentDate"
  | "terminationInvoiceName"
  | "terminationInvoiceId"
  | "hasTerminationPayrollAdvice"
  | "terminationPayrollAdviceSentDate"
  | "terminationPayrollAdviceName"
  | "noteToEmployer"
  | "noteFromEmployer"
  | "driverNotifiedOfTermination"
>;

export type DossierPaginationList = PaginationResponse<Dossier>;

export type DossierPhaseEvent = {
  id: string;
  eventId: number;
  eventName: string;
  eventTriggeredBy: DossierEventTriggeredBy;
  visibility: DossierEventVisibility;
  completed: boolean;
  completedBy: string | null;
  completedOn: string | null;
  completionText: string;
  completionTitle: string | null;
  showCompletionDate: boolean;
};

export type DossierPhase = {
  id: number;
  name: string;
  events: DossierPhaseEvent[];
  completed: boolean;
};

export type DetailedDossierStatus = {
  dossierId: string;
  dossierType: DossierType;
  dossierPhases: DossierPhase[];
  nextDriverAction: NextDriverAction;
  currentPhaseId: number | null;
};

export type FinancialApplicationPersonalInfo = {
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  mobile: string | null;
  phone: string | null;
  numberOfDependents: number | null;
  maritalStatus: MaritalStatus | null;
};

export type FinanceApplicationIdentity = {
  hasDriversLicense: boolean;
  driversLicense: {
    type: DriversLicenseType | null;
    number: string | null;
    cardNumber: string | null;
    frontImageUrl: string | null;
    frontImageName: string | null;
    backImageUrl: string | null;
    backImageName: string | null;
    state: State | null;
  };
  residency: {
    status: AustralianResidencyStatus | null;
    passportImageUrl: string | null;
    passportImageName: string | null;
    visaImageUrl: string | null;
    visaImageName: string | null;
    visaType: string | null;
    visaExpiry: string | null;
  };
};

export type FinancialApplicationAddress = {
  current: {
    streetAddress: string | null;
    suburb: string | null;
    state: State | null;
    postcode: string | null;
    residenceStatus: AddressResidenceStatus | null;
    yearsAt: number | null;
    monthsAt: number | null;
    landlord: string | null;
    landlordPhone: string | null;
  };
  previous: {
    streetAddress: string | null;
    suburb: string | null;
    state: State | null;
    postcode: string | null;
    yearsAt: number | null;
    monthsAt: number | null;
  };
};

export type FinancialApplicationEmployment = {
  hasPayslip: boolean;
  latestPayslipUrl: string | null;
  latestPayslipName: string | null;
  prevPayslipUrl: string | null;
  prevPayslipName: string | null;
  annualSalary: {
    total: number | null;
    perPay: number | null;
    payFrequency: PayFrequency | null;
    netPayPerMonth: number | null;
  };
  current: {
    type: EmploymentType | null;
    jobTitle: string | null;
    employerName: string | null;
    employerContactName: string | null;
    employerContactPhone: string | null;
    lastTaxReturnUrl: string | null;
    lastTaxReturnName: string | null;
    prevTaxReturnUrl: string | null;
    prevTaxReturnName: string | null;
    yearsAt: number | null;
    monthsAt: number | null;
  };
  previous: {
    jobTitle: string | null;
    employerName: string | null;
    employerContactName: string | null;
    employerContactPhone: string | null;
    yearsAt: number | null;
    monthsAt: number | null;
  };
};

export type FinancialApplicationIncome = {
  investmentPropertyIncome: number | null;
  investmentIncome: number | null;
  govtBenefitIncome: number | null;
  otherIncome: number | null;
  rent: {
    amount: number | null;
    frequency: PayFrequency | null;
    monthlyAmount: number | null;
  };
  mortgage: {
    amount: number | null;
    frequency: PayFrequency | null;
    monthlyAmount: number | null;
  };
  repayments: {
    amount: number | null;
    frequency: PayFrequency | null;
    monthlyAmount: number | null;
  };
  vehicleCosts: {
    amount: number | null;
    frequency: PayFrequency | null;
    monthlyAmount: number | null;
  };
  educationCosts: {
    amount: number | null;
    frequency: PayFrequency | null;
    monthlyAmount: number | null;
  };
  generalExpenses: {
    amount: number | null;
    frequency: PayFrequency | null;
    monthlyAmount: number | null;
  };
  otherExpenses: {
    amount: number | null;
    frequency: PayFrequency | null;
    monthlyAmount: number | null;
  };
  spouse: {
    include: boolean;
    annualSalary: number | null;
    salaryPerPay: number | null;
    netPayPerMonth: number | null;
    payFrequency: PayFrequency | null;
    employmentType: EmploymentType | null;
    lastTaxReturnUrl: string | null;
    lastTaxReturnName: string | null;
    prevTaxReturnUrl: string | null;
    prevTaxReturnName: string | null;
    latestPayslipUrl: string | null;
    latestPayslipName: string | null;
    incomeDeclarationUrl: string | null;
    incomeDeclarationName: string | null;
  };
  totalMonthlyIncome: number | null;
  totalMonthlyExpenses: number | null;
  netMonthlyIncome: number | null;
};

export type FinancialApplicationCreditCards = {
  creditCardProviders: string | null;
  totalCreditCardLimit: number | null;
  totalCreditCardBalance: number | null;
};

export type FinancialApplicationAssetsAndLiabilitiesTotals = {
  totalAssets: number | null;
  totalLiabilities: number | null;
  totalNetWorth: number | null;
};

export type AssetAndLiabilityType = {
  id: string;
  name: string;
  typeId: number;
  typeLabel: string;
  isAsset: boolean;
  isLiability: boolean;
  isActive: boolean;
};

export type AssetAndLiability = {
  id: string;
  financeId: string;
  name: string;
  description: string | null;
  assetValue: number | null;
  liabilityValue: number | null;
  typeId: number | null;
  typeLabel: string | null;
  displayOrder: number | null;
  financier: string | null;
  incomePerMth: number | null;
  expensePerMth: number | null;
  disposePriorToSettlement: boolean | null;
};

export type FinanceApplication = {
  id: string;
  isLocked: boolean;
  isHidden: boolean;
  status: FinanceStatus;
  financier: string;
  personal: FinancialApplicationPersonalInfo;
  identity: FinanceApplicationIdentity;
  address: FinancialApplicationAddress;
  employment: FinancialApplicationEmployment;
  income: FinancialApplicationIncome;
  assetsAndLiabilities: AssetAndLiability[];
  assetsAndLiabilitiesTotals: FinancialApplicationAssetsAndLiabilitiesTotals;
  creditCards: FinancialApplicationCreditCards;
};

export const DossierNextActionRouteMap: Record<NextDriverAction, string> = {
  "request-employer-setup": "/employer-setup",
  "finance-application": "/finance-application",
  "packaging-agreement": "/packaging-agreement",
  "direct-debit": "/direct-debit",
  "no-action-required": "/action-confirmation/not-ready",
  "select-vehicle-sourcing-method": "/vehicle-sourcing-method",
};

export const buildNextActionRoute = (
  dossierId: string,
  nextAction: NextDriverAction,
  fromConfirmation: boolean = false
) => {
  const nextActionPath = DossierNextActionRouteMap[nextAction];

  return `/dossiers/${dossierId}${nextActionPath}${
    fromConfirmation ? "?fromConfirmation=true" : ""
  }`;
};
